import { nanoid } from "@repo/utils/nanoid";

type RouteVisitListener = (routeVisitId: string) => void;

interface RouteVisitRouterEvent {
  hrefChanged?: boolean;
}

interface RouteVisitRouter {
  subscribe(
    eventType: "onResolved",
    listener: (event: RouteVisitRouterEvent) => void,
  ): () => void;
}

const listeners = new Set<RouteVisitListener>();
let currentRouteVisitId = createRouteVisitId();
let stopListeningToRouter: (() => void) | null = null;

function createRouteVisitId(): string {
  return `visit_${nanoid()}`;
}

function publishRouteVisitId(nextRouteVisitId: string) {
  currentRouteVisitId = nextRouteVisitId;
  for (const listener of listeners) {
    listener(nextRouteVisitId);
  }
}

export function getRouteVisitId(): string {
  return currentRouteVisitId;
}

export function rotateRouteVisitId(): string {
  const nextRouteVisitId = createRouteVisitId();
  publishRouteVisitId(nextRouteVisitId);
  return nextRouteVisitId;
}

export function subscribeToRouteVisit(
  listener: RouteVisitListener,
): () => void {
  listeners.add(listener);
  listener(currentRouteVisitId);

  return () => {
    listeners.delete(listener);
  };
}

export function initializeRouteVisit(router: RouteVisitRouter): void {
  if (stopListeningToRouter) {
    return;
  }

  stopListeningToRouter = router.subscribe("onResolved", (event) => {
    if (event.hrefChanged) {
      rotateRouteVisitId();
    }
  });
}

export function setRouteVisitIdForTests(routeVisitId: string): void {
  publishRouteVisitId(routeVisitId);
}

export function resetRouteVisitForTests(): void {
  stopListeningToRouter?.();
  stopListeningToRouter = null;
  listeners.clear();
  currentRouteVisitId = createRouteVisitId();
}
